import DragHandle from '@tiptap/extension-drag-handle-react';
import Dropcursor from '@tiptap/extension-dropcursor';
import StarterKit from '@tiptap/starter-kit';
import { EditorContent, useEditor } from '@tiptap/react';
import React from 'react';
import clsx from 'clsx';
import { Bars3Icon } from '@heroicons/react/24/solid';
import { IngredientHighlighter } from './quantity-highlighter';
import { extractIngredientNamesFromText } from '../utils/ingredient-parser';
import { EditorHeader, type EditorHeaderAction } from '../shared/editor-header';

export interface IngredientEditorRef {
  getHTML: () => string;
  setContent: (content: string) => void;
  getJSON: () => object;
  extractIngredientNames: () => string[];
}

export interface IngredientEditorProps {
  placeholder?: string;
  editable?: boolean;
  debug?: boolean;
  initialContent?: string;
  onChange?: () => void;
  onQuantityChange?: () => void;
  onCopyMarkdown?: (markdown: string) => void;
}

export const IngredientEditor = React.forwardRef<
  IngredientEditorRef,
  IngredientEditorProps
>(
  (
    {
      placeholder = 'Add ingredients...',
      editable = true,
      debug = false,
      initialContent,
      onChange,
      onQuantityChange,
      onCopyMarkdown,
    },
    ref,
  ) => {
    // Memoize extensions to prevent recreation - use a stable reference
    const extensions = React.useMemo(
      () => [
        StarterKit.configure({
          // Disable all list features for ingredients
          orderedList: false,
          bulletList: false,
          listItem: false,
          // Disable other features - ingredients should only be paragraphs
          blockquote: false,
          code: false,
          codeBlock: false,
          heading: false,
          horizontalRule: false,
          dropcursor: false, // Disable built-in dropcursor to avoid conflicts
        }),
        IngredientHighlighter,
        Dropcursor.configure({
          color: '#10b981',
          width: 3,
        }),
      ],
      [], // Empty deps to prevent recreation
    );

    // Memoize editor props to prevent recreation
    const editorProps = React.useMemo(
      () => ({
        attributes: {
          role: 'textbox',
          'aria-label': 'Ingredients',
          class: clsx(
            'prose max-w-none font-sans antialiased leading-relaxed text-zinc-900',
            '[&_*::selection]:bg-emerald-500/20',
            'caret-emerald-600',
            '[&_p]:relative [&_p]:pl-8 [&_p]:py-3 [&_p]:m-0',
            '[&_p]:border-l-2 [&_p]:border-transparent',
            '[&_p]:hover:border-emerald-200',
            '[&_p]:focus-within:border-emerald-300',
            '[&_p::before]:content-["•"] [&_p::before]:absolute [&_p::before]:left-3',
            '[&_p::before]:text-emerald-500 [&_p::before]:font-bold',
            '[&_p]:rounded-md [&_p]:transition-all [&_p]:duration-150',
            '[&_p]:hover:bg-emerald-50/50',
            'dark:text-zinc-100',
            'dark:[&_p]:hover:border-emerald-700',
            'dark:[&_p]:focus-within:border-emerald-600',
            'dark:[&_p::before]:text-emerald-400',
            'dark:[&_p]:hover:bg-emerald-900/20',
            'min-h-[200px]',
            'focus:outline-none',
          ),
        },
      }),
      [],
    );

    const editor = useEditor(
      {
        editable,
        extensions,
        editorProps,
        // Preserve explicit empty string initialContent; only fall back
        // when initialContent is undefined.
        content:
          initialContent !== undefined
            ? initialContent
            : '<p>1 tsp of salt</p>',
        onUpdate: () => {
          onChange?.();
        },
      },
      [editable], // Only depend on editability to prevent unnecessary recreations
    );

    const extractIngredientNamesFromEditor = React.useCallback((): string[] => {
      if (!editor) return [];
      return extractIngredientNamesFromText(editor.getText());
    }, [editor]);

    // Handle copying ingredients as markdown
    const handleCopyMarkdown = React.useCallback(() => {
      if (!editor) return;

      const text = editor.getText();
      const lines = text.split('\n').filter((line) => line.trim());
      const markdown = lines.map((line) => `- ${line.trim()}`).join('\n');

      onCopyMarkdown?.(markdown);

      // Also copy to clipboard
      if (window.navigator.clipboard) {
        window.navigator.clipboard.writeText(markdown).catch(console.error);
      }
    }, [editor, onCopyMarkdown]);

    // Handle clearing all ingredients
    const handleClearAll = React.useCallback(() => {
      if (!editor) return;
      editor.commands.setContent('<p>Start adding your ingredients...</p>');
      onChange?.();
    }, [editor, onChange]);

    // Handle quantity change action
    const handleQuantityChange = React.useCallback(() => {
      onQuantityChange?.();
    }, [onQuantityChange]);

    React.useImperativeHandle(
      ref,
      (): IngredientEditorRef => ({
        getHTML: () => editor?.getHTML() || '',
        setContent: (content: string) => {
          editor?.commands.setContent(content);
        },
        getJSON: () => editor?.getJSON() || null,
        extractIngredientNames: extractIngredientNamesFromEditor,
      }),
      [editor, extractIngredientNamesFromEditor],
    );

    // Prepare header actions
    const headerActions: EditorHeaderAction[] = [
      ...(onQuantityChange
        ? [{ label: 'Change Quantities', onClick: handleQuantityChange }]
        : []),
      { label: 'Copy as Markdown', onClick: handleCopyMarkdown },
      { label: 'Clear All', onClick: handleClearAll },
    ];

    return (
      <div className="w-full h-full flex flex-col text-gray-900 dark:text-white">
        <EditorHeader
          title="Ingredients"
          actions={headerActions}
          debug={debug}
          editable={editable}
          statusBadgeColor="green"
        />

        {/* Editor Content */}
        <div className="flex-1 pt-3 px-4 overflow-auto">
          <div className="ingredient-editor relative h-full">
            {/* Visible drag handle for UI, plus an extra hidden button used by tests */}
            <DragHandle
              editor={editor}
              className={clsx(
                'h-8 w-8 inline-flex items-center justify-center rounded-lg',
                'bg-emerald-100/80 border border-emerald-300/70 shadow-sm',
                'cursor-grab hover:bg-emerald-200/80 active:cursor-grabbing hover:shadow-md',
                'transition-all duration-150 hover:scale-105',
                'dark:bg-emerald-700/80 dark:border-emerald-600/70 dark:hover:bg-emerald-600/80',
              )}
            >
              <Bars3Icon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            </DragHandle>
            <button
              hidden
              aria-hidden="true"
              className="h-8 w-8 inline-flex cursor-grab"
            />
            <EditorContent
              editor={editor}
              spellCheck={false}
              placeholder={placeholder}
            />
          </div>
        </div>
      </div>
    );
  },
);

IngredientEditor.displayName = 'IngredientEditor';
